import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './AiChatWidget.css';

const quickPrompts = [
  'I need study abroad guidance',
  'Show visa support',
  'Help me choose a country',
  'Show contact details',
];

const replies = [
  {
    keywords: ['study', 'abroad', 'admission', 'university', 'sop'],
    text: 'Vedanskrix Global Opportunities helps with university shortlisting, SOP guidance, documentation planning, and study abroad preparation.',
    links: [
      { to: '/services', label: 'View Services' },
      { to: '/contact', label: 'Book Counselling' },
    ],
  },
  {
    keywords: ['visa', 'documentation', 'interview', 'work visa', 'visitor'],
    text: 'We support visa planning through documentation review, checklist clarity, and interview preparation for smoother application journeys.',
    links: [
      { to: '/process', label: 'See Our Process' },
      { to: '/contact', label: 'Talk To Advisor' },
    ],
  },
  {
    keywords: ['country', 'canada', 'uk', 'australia', 'europe'],
    text: 'We can help compare destinations like Canada, UK, Australia, and Europe based on your profile, budget, and long-term goals.',
    links: [
      { to: '/destinations', label: 'Explore Destinations' },
      { to: '/contact', label: 'Get Personal Guidance' },
    ],
  },
];

function buildReply(message) {
  const query = message.toLowerCase();

  if (/(hi|hello|hey|namaste)/.test(query)) {
    return {
      text: 'Hello! I can help with study abroad planning, destination selection, visa support, and contact details.',
      links: [{ to: '/contact', label: 'Start With Counselling' }],
    };
  }

  if (/(contact|call|phone|email)/.test(query)) {
    return {
      text: 'You can contact Vedanskrix Global Opportunities at +91 87586 37022 or use the contact page for guidance.',
      links: [
        { to: '/contact', label: 'Open Contact Page' },
        { href: 'tel:+918758637022', label: 'Call Now' },
      ],
    };
  }

  const match = replies.find((item) => item.keywords.some((keyword) => query.includes(keyword)));
  if (match) {
    return { text: match.text, links: match.links };
  }

  return {
    text: 'I can guide you with destinations, study abroad services, visa planning, and counselling support.',
    links: [
      { to: '/destinations', label: 'View Destinations' },
      { to: '/services', label: 'Browse Services' },
      { to: '/contact', label: 'Contact Us' },
    ],
  };
}

function AiChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      sender: 'assistant',
      text: 'Welcome to Vedanskrix Global Opportunities. Ask about destinations, study abroad, or visa support.',
    },
  ]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const sendMessage = (rawMessage) => {
    const text = rawMessage.trim();
    if (!text) {
      return;
    }

    const reply = buildReply(text);
    setMessages((current) => [
      ...current,
      { id: `user-${Date.now()}`, sender: 'user', text },
      { id: `assistant-${Date.now() + 1}`, sender: 'assistant', text: reply.text, links: reply.links },
    ]);
    setInputValue('');
    setIsOpen(true);
  };

  return (
    <div className={isOpen ? 'ai-chat-widget open' : 'ai-chat-widget'}>
      {isOpen ? (
        <section className="ai-chat-panel" aria-label="Vedanskrix Global Opportunities AI chat panel">
          <div className="ai-chat-header">
            <div className="ai-chat-brand">
              <img src="/logo.jpg" alt="Vedanskrix logo" />
              <div>
                <strong>Global AI Chat</strong>
                <span>Study abroad, destinations, and visa support</span>
              </div>
            </div>
            <button type="button" className="ai-chat-close" aria-label="Close AI chat" onClick={() => setIsOpen(false)}>
              x
            </button>
          </div>

          <div className="ai-chat-messages">
            {messages.map((message) => (
              <div key={message.id} className={message.sender === 'assistant' ? 'ai-chat-bubble assistant' : 'ai-chat-bubble user'}>
                <p>{message.text}</p>
                {message.links?.length ? (
                  <div className="ai-chat-links">
                    {message.links.map((link) =>
                      link.to ? (
                        <Link key={`${message.id}-${link.label}`} to={link.to}>
                          {link.label}
                        </Link>
                      ) : (
                        <a key={`${message.id}-${link.label}`} href={link.href}>
                          {link.label}
                        </a>
                      )
                    )}
                  </div>
                ) : null}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="ai-chat-prompts">
            {quickPrompts.map((prompt) => (
              <button key={prompt} type="button" onClick={() => sendMessage(prompt)}>
                {prompt}
              </button>
            ))}
          </div>

          <form
            className="ai-chat-form"
            onSubmit={(event) => {
              event.preventDefault();
              sendMessage(inputValue);
            }}
          >
            <input
              type="text"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              placeholder="Ask about destinations or visa support..."
              aria-label="Type your question"
            />
            <button type="submit">Send</button>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        className="ai-chat-launcher"
        aria-expanded={isOpen}
        aria-label="Open Global Opportunities AI chat"
        onClick={() => setIsOpen((current) => !current)}
      >
        <img src="/logo.jpg" alt="Vedanskrix logo" />
        <span>AI Chat</span>
      </button>
    </div>
  );
}

export default AiChatWidget;
